import { Component, Input } from '@angular/core';
import { DemoMessageModalComponent } from 'app/shared/modals/demo-message-modal/demo-message-modal.component';
import { ImageViewerModalComponent } from 'app/teacher/modals/image-viewer-modal/image-viewer-modal.component';
import { LocalizedToasterService } from 'app/core/toaster/localized-toaster.service';
import { ModalService } from 'app/core/modals/services/modal.service';
import { SolutionFeedbackService } from 'app/core/solution/solution-feedback.service';
import { SolutionModel } from 'app/teacher/models/solution.model';
import { tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UrlService } from 'app/core/http/url.service';

@UntilDestroy()
@Component({
    selector: 'kh-solution-display',
    styleUrls: ['./solution-display.component.less'],
    templateUrl: './solution-display.component.html',
    standalone: false
})
export class SolutionDisplayComponent {
  // Boolean used to set initial state. Also used as a forced toggle.
  @Input() public set hideFeedback(value: boolean) {
    this.showFeedback = !value;
  }

  @Input() public showStudentName = false; // Boolean used to hide/show student name from parent
  @Input() public layoutClass = 'basic'; // Modifier for base class
  @Input() public hideExtras = false;
  @Input() public hideActions = false;
  @Input() public isDemo = false;
  @Input() public showFeedback = false;

  public showDeletePopover = false;
  public url = '';
  public editing = false;

  // TODO: need to implement
  // public tinymceLiteOptions = $rootScope.TinymceLiteOptions;
  public tinymceLiteOptions: Record<string, any> = {};
  private solutionObj!: SolutionModel;

  public constructor(private readonly modalService: ModalService,
    private readonly toaster: LocalizedToasterService,
    private readonly solutionFeedbackService: SolutionFeedbackService,
    private readonly urlService: UrlService) { }

  // Folderpath for tinymce. @ so that it shows up as a string.
  @Input()
  public set tinymceFolder(tinymceFolder: string) {
    this.tinymceLiteOptions.folder = tinymceFolder;
  }

  public get solution(): SolutionModel {
    return this.solutionObj;
  }

  // Solution to display; object reference
  @Input()
  public set solution(solution: SolutionModel) {
    if (solution.SolutionFileId) {
      this.url = this.urlService.buildServerApiUrl() + '/File/' + solution.SolutionFileId + '?Random=' + Math.floor(Math.random() * 1000);
    }
    this.solutionObj = solution;
  }

  public toggleFavourite(solution: SolutionModel): void {
    if (this.isDemo) {
      this.modalService.displayModal(DemoMessageModalComponent);

      return;
    }

    this.solutionFeedbackService.updateMyFavouriteNo(
      solution.AttemptId,
      solution.AttemptItemOrder,
      solution.IsMyFavouriteNo ? 'UndoMyFavouriteNo' : 'MyFavouriteNo'
    ).pipe(
      tap(() => {
        solution.IsMyFavouriteNo = !solution.IsMyFavouriteNo;
        if (solution.IsMyFavouriteNo) {
          this.toaster.success('SOLUTION_DISPLAY.SAVED_AS_A_FAVOURITE');
        }
      }),
      untilDestroyed(this)
    )
      .subscribe();
  }

  public toggleLike(solution: SolutionModel): void {
    if (this.isDemo) {
      this.modalService.displayModal(DemoMessageModalComponent);

      return;
    }

    this.solutionFeedbackService.updateSolutionLike(
      solution.AttemptId,
      solution.AttemptItemOrder,
      solution.IsLiked ? 'Unlike' : 'Like'
    ).pipe(
      tap(() => solution.IsLiked = !solution.IsLiked),
      untilDestroyed(this)
    )
      .subscribe();
  }

  public saveComment(solution: SolutionModel): void {
    if (this.isDemo) {
      this.modalService.displayModal(DemoMessageModalComponent);

      return;
    }

    if (!solution.TeacherComments) {
      solution.TeacherComments = '';
    }

    this.solutionFeedbackService.setSolutionComment(
      solution.AttemptId,
      solution.AttemptItemOrder,
      solution.TeacherComments
    ).pipe(
      tap(() => this.toaster.success('SOLUTION_DISPLAY.COMMENT_SAVED')),
      untilDestroyed(this)
    )
      .subscribe();
  }

  public rotateSolution(solution: SolutionModel): void {
    if (this.isDemo) {
      this.modalService.displayModal(DemoMessageModalComponent);

      return;
    }

    this.solutionFeedbackService.rotateSolution(solution.SolutionFileId).pipe(
      tap(() => this.url += 'R'),
      untilDestroyed(this)
    )
      .subscribe();
  }

  public expandImage() {
    this.modalService.displayModal<ImageViewerModalComponent>(ImageViewerModalComponent, { imageUrl: this.url }, 'xl', true);
  }

  public delete(solution: SolutionModel): void {
    if (this.isDemo) {
      this.modalService.displayModal(DemoMessageModalComponent);

      return;
    }

    this.solutionFeedbackService.deleteSolution(solution.AttemptId, solution.AttemptItemOrder).pipe(
      tap(() => solution.IsRemoved = true),
      untilDestroyed(this)
    )
      .subscribe();
  }

  public toggleShowFeedback(): void {
    this.showFeedback = !this.showFeedback;
  }
}
